import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { FormControl } from '@mui/material';
import { CheckboxDropdown, Skeleton } from '@repo/videa-components/src';
import { isToday } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';

import {
  blueBlack400,
  blueBlack640,
  blueUltramarine200,
  orange100,
  orange700,
  transparent,
} from '../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';

import {
  operatoryFilterAtom,
  operatoryListAtom,
  selectDateAtom,
} from './atoms';

export const StyledFormControl = styled(FormControl)`
  .MuiFormLabel-root {
    background-color: ${transparent};
    font-family: 'Neue Haas Grotesk Display Pro', sans-serif;
    font-weight: 600;
    font-size: 0.875em;
    transform: translate(0.625em, 0.5em);
    color: ${blueBlack640} !important;
  }

  .MuiInputBase-root {
    background-color: #f7f8fb;
    box-sizing: border-box;
    border: 0;
    min-height: 3.25em;
    font-family: 'Neue Haas Grotesk Display Pro', sans-serif;
    border: 2px solid transparent;
    color: ${blueBlack400};
    border-radius: 0.375em;

    &:hover,
    &:focus,
    &:active {
      border: 2px solid ${blueUltramarine200};
    }

    .MuiInputBase-input {
      padding: 1em 1.5em 0 0.5em;
    }

    .MuiSvgIcon-root {
      transform: translate(0, 0.25em);
    }

    &.highlight {
      border-color: ${orange100};
      color: ${orange700};
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: 0;
  }
`;

interface Props {
  loading?: boolean;
  source?: 'PATIENT_LIST' | 'HUDDLE_PRINTOUT';
}

export const OperatoriesDropdown = ({ loading, source }: Props) => {
  const operatoryList = useAtomValue(operatoryListAtom);
  const [selectedOperatories, setSelectedOperatories] =
    useAtom(operatoryFilterAtom);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();

  const selectedDate = useAtomValue(selectDateAtom);

  const handleChange = (newOperatories: string[]) => {
    trackEvent(mixpanelEvents.selectOperatory, {
      source,
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
    });
    setSelectedOperatories(newOperatories);
  };

  if (loading) {
    return <Skeleton className="vai-h-11 vai-w-40" />;
  }

  return (
    <CheckboxDropdown
      items={operatoryList}
      onChange={handleChange}
      placeholder={
        selectedOperatories.length ? t`Operatories` : t`All Operatories`
      }
      getItemLabel={(item) => item}
      getItemValue={(item) => item}
      selectedItems={selectedOperatories}
      type="operatories"
    />
  );
};
